// src/components/property/ImageThumbnailGrid.tsx
/**
 * ImageThumbnailGrid Component
 *
 * Display-only grid of processed images or placeholder skeletons.
 * Drag-and-drop reorder functionality has been moved to OriginalImagesDialog.
 */

import { useMemo } from 'react';
import { useQuery } from 'convex/react';
import { api } from '../../../convex/_generated/api';
import { cn } from '@/lib/utils';
import { Skeleton } from '@/components/ui/skeleton';
import { ImagePlaceholderSkeleton } from './ImagePlaceholderSkeleton';
import type { IImageThumbnailGridProps, IImageSceneType } from '@/@types/property';
import type { Id } from '../../../convex/_generated/dataModel';

// Scene type display labels
const SCENE_TYPE_LABELS: Record<IImageSceneType, string> = {
  exterior: 'Exterior',
  living_room: 'Living Room',
  kitchen: 'Kitchen',
  bedroom: 'Bedroom',
  bathroom: 'Bathroom',
  balcony: 'Balcony',
  backyard: 'Backyard',
  pool: 'Pool',
  garden: 'Garden',
  parking: 'Parking',
  garage: 'Garage',
  plan: 'Floor Plan',
  other: 'Other',
};

interface IProcessedImageThumbnailProps {
  storageId: Id<'_storage'>;
  index: number;
  sceneType?: IImageSceneType;
  aspectRatio: '16:9' | '9:16';
  onClick: () => void;
}

function ProcessedImageThumbnail({
  storageId,
  index,
  sceneType,
  aspectRatio,
  onClick,
}: IProcessedImageThumbnailProps) {
  const imageUrl = useQuery(api.storage.getUrl, { storageId });

  const label = sceneType ? SCENE_TYPE_LABELS[sceneType] : undefined;
  const aspectClass = aspectRatio === '9:16' ? 'aspect-[9/16]' : 'aspect-video';

  return (
    <div
      role="button"
      tabIndex={0}
      className={cn(
        'relative group rounded-lg overflow-hidden bg-muted cursor-pointer',
        aspectClass,
        'border-2 border-transparent hover:border-primary transition-colors'
      )}
      onClick={onClick}
      onKeyDown={e => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      }}
    >
      {/* Scene type badge */}
      {label && (
        <div className="absolute bottom-2 left-2 z-10 px-2 py-1 rounded bg-black/70 text-white text-xs font-medium">
          {label}
        </div>
      )}

      {/* Image */}
      {imageUrl ? (
        <img
          src={imageUrl}
          alt={`Processed property image ${index + 1}`}
          className="w-full h-full object-cover"
          draggable={false}
        />
      ) : (
        <Skeleton className="w-full h-full" />
      )}
    </div>
  );
}

function ImageThumbnailGrid({
  imageIds,
  sceneTypes,
  excludedIndices,
  aspectRatio,
  processedImageIds,
  showPlaceholders,
  placeholderCount,
  onImageClick,
}: IImageThumbnailGridProps) {
  // Memoize excludedSet to prevent unnecessary re-renders
  const excludedSet = useMemo(() => new Set(excludedIndices), [excludedIndices]);

  // Filter to active (non-excluded) images for mapping scene types
  const activeImages = useMemo(
    () =>
      imageIds.map((id, index) => ({ id, index })).filter(({ index }) => !excludedSet.has(index)),
    [imageIds, excludedSet]
  );

  // Scene type lookup - memoized for performance
  const sceneTypeMap = useMemo(
    () => new Map(sceneTypes.map(st => [st.index, st.sceneType as IImageSceneType])),
    [sceneTypes]
  );

  // Grid column classes based on aspect ratio
  const gridClass = cn(
    'grid gap-4',
    aspectRatio === '9:16'
      ? 'grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6'
      : 'grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5'
  );

  // Show placeholders when no processed images exist
  if (showPlaceholders) {
    return (
      <div className={gridClass}>
        {Array.from({ length: placeholderCount }).map((_, index) => (
          <ImagePlaceholderSkeleton
            key={`placeholder-${index}`}
            aspectRatio={aspectRatio}
            index={index}
          />
        ))}
      </div>
    );
  }

  // Show processed images
  if (!processedImageIds || processedImageIds.length === 0) {
    return (
      <div className="flex items-center justify-center py-12 text-muted-foreground">
        No processed images available
      </div>
    );
  }

  return (
    <div className={gridClass}>
      {processedImageIds.map((storageId, displayIndex) => {
        // Map display index to original image index for scene type lookup
        const originalIndex = activeImages[displayIndex]?.index;
        const sceneType = originalIndex !== undefined ? sceneTypeMap.get(originalIndex) : undefined;

        return (
          <ProcessedImageThumbnail
            key={storageId.toString()}
            storageId={storageId}
            index={displayIndex}
            sceneType={sceneType}
            aspectRatio={aspectRatio}
            onClick={() => onImageClick(displayIndex)}
          />
        );
      })}
    </div>
  );
}

export { ImageThumbnailGrid };
